import { Dispatch, SetStateAction } from 'react';
import { Button, Spinner, SpinnerSize, Tag } from '@blueprintjs/core';
import { ace_index } from '@axo/shared/data-access/types';
import { AceCard } from '../AceCard';
import { IconMenu } from '../IconMenu';
import { ReorderWorkingLists, useReorder } from './ReorderWorkingLists';
import { SelectCard } from './SelectCard';
import { EditCard } from './EditCard';
import { ListFilter } from './ListFilter';
import { usePersistedState } from '../../hooks/utils/usePersistedState';
import { FilterSet } from '../FilterSet';

interface Props {
  lists: ace_index.WorkingList[];
  loading: boolean;

  currentList: ace_index.WorkingList | undefined;
  setCurrentListID: (id: string | undefined) => void;

  mode: 'select' | 'edit' | 'reorder';
  setMode: (mode: Props['mode']) => void;

  mutating: boolean;

  onCreate: () => void;

  editable: boolean;
  onEdit: (list: ace_index.WorkingList) => void;
  setEditState: Dispatch<SetStateAction<ace_index.WorkingList>>;
  onSave: () => void;
  onCancel: () => void;

  onDelete: (list: ace_index.WorkingList) => void;
}

export function WorkingListsPanel({
  lists,
  loading,
  currentList,
  setCurrentListID,
  mode,
  setMode,
  mutating,
  onCreate,
  editable,
  onEdit,
  setEditState,
  onSave,
  onCancel,
  onDelete,
}: Props) {
  const reorder = useReorder(lists);

  const [filter, setFilter] = usePersistedState(
    'ace.workinglist.filter.v2',
    {} as ace_index.Audience,
    window.sessionStorage
  );

  const filteredLists = lists.filter(filterListsPredicate(mode, filter));

  if (loading) {
    return (
      <AceCard
        id="WorkingListsPanel"
        noBorder
        title={editable ? 'Manage Lists' : 'Lists'}
      >
        <Spinner size={SpinnerSize.SMALL} />
      </AceCard>
    );
  }

  return (
    <AceCard id="WorkingListsPanel" title={editable ? 'Manage Lists' : 'Lists'}>
      {(editable || mode === 'select') && (
        <IconMenu
          leftMenu={
            <ListFilter
              disabled={mode !== 'select'}
              filter={filter}
              setFilter={setFilter}
            />
          }
        >
          <>
            {mode === 'select' && editable && (
              <>
                <Button
                  icon="add"
                  text="Add new"
                  intent="primary"
                  disabled={mutating}
                  onClick={onCreate}
                />

                <Button
                  icon="move"
                  text="Reorder"
                  minimal
                  disabled={mutating}
                  onClick={() => setMode('reorder')}
                />
              </>
            )}

            {mode === 'reorder' && (
              <>
                <Tag icon="move" minimal>
                  Drag and drop working lists to reorder.
                </Tag>

                <Button
                  icon="floppy-disk"
                  text="Save"
                  minimal
                  intent="primary"
                  loading={reorder.isLoading}
                  disabled={!reorder.hasChanges}
                  onClick={async () => {
                    await reorder.save();
                    setMode('select');
                  }}
                />

                <Button
                  icon="cross"
                  text="Cancel"
                  intent="warning"
                  minimal
                  onClick={() => setMode('select')}
                />
              </>
            )}
          </>
        </IconMenu>
      )}

      {mode === 'reorder' && <ReorderWorkingLists reorder={reorder} />}

      {(mode === 'select' || mode === 'edit') && (
        <FilterSet>
          {filteredLists.length < 1 && (
            <p>
              <em>
                No working lists available. Contact your manager to get access
                to working lists.
              </em>
            </p>
          )}

          {filteredLists.map((list) =>
            mode === 'edit' && currentList && list.ID === currentList.ID ? (
              <EditCard
                key={list.ID}
                loading={mutating}
                listDetails={currentList}
                setListDetails={setEditState}
                onCancel={onCancel}
                onSave={onSave}
              />
            ) : (
              <SelectCard
                key={list.ID}
                editable={editable}
                disabled={mode !== 'select'}
                listDetails={list}
                onSelect={() => setCurrentListID(list.ID)}
                isSelected={list.ID === currentList?.ID}
                onEdit={() => onEdit(list)}
                onDelete={() => onDelete(list)}
              />
            )
          )}
        </FilterSet>
      )}
    </AceCard>
  );
}

function filterListsPredicate(mode: string, filter: ace_index.Audience) {
  return (list: ace_index.WorkingList): boolean => {
    if (mode !== 'select') {
      // Only filter in select mode.
      return true;
    }

    const noOverlap = (
      filters: string[] | undefined,
      values: string[] | undefined
    ) =>
      filters &&
      filters.length > 0 &&
      values?.find((value) => filters.includes(value)) === undefined;

    if (noOverlap(filter.MarketCountries, list.Audience.MarketCountries)) {
      return false;
    }

    if (noOverlap(filter.Roles, list.Audience.Roles)) {
      return false;
    }

    if (noOverlap(filter.ClientIDs, list.Audience.ClientIDs)) {
      return false;
    }

    return true;
  };
}
